/**
 * WidgetRenderer - Renders dashboard widgets based on chart_type
 * Supports: bar, line, pie, scatter, sparkline, funnel, kpi_card, activity_feed, list, table, calendar
 */
import { Box, Typography, List, ListItem, ListItemText, Chip, Avatar } from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import { PieChart } from '@mui/x-charts/PieChart';
import { LineChart } from '@mui/x-charts/LineChart';
import { ScatterChart } from '@mui/x-charts/ScatterChart';
import { SparkLineChart } from '@mui/x-charts/SparkLineChart';
import { TrendingUp, TrendingDown, Calendar, MessageSquare, FileText, DollarSign, Users, Building2 } from 'lucide-react';
import { getMockDataForWidget } from './mockDashboardData';

/**
 * Check if chart_config has actual data
 */
function hasData(config: WidgetConfig | undefined): boolean {
  if (!config) return false;
  // Check for various data fields
  if (config.data?.length) return true;
  if (config.scatterData?.length) return true;
  if (config.activities?.length) return true;
  if (config.items?.length) return true;
  if (config.stages?.length) return true;
  if (config.value !== undefined) return true;
  return false;
}

/**
 * Get mock data for a widget based on module and widget name
 */
function useMockData(module: string | undefined, widgetName: string, _chartType: string): WidgetConfig {
  if (!module) return {};
  return getMockDataForWidget(module, widgetName);
}

interface ChartData {
  label: string;
  value: number;
}

interface ScatterData {
  x: number;
  y: number;
  id: string;
  label?: string;
  size?: number;
}

interface ActivityItem {
  id: string;
  type: string;
  description: string;
  timestamp: string;
  entity?: string;
}

interface ListItemData {
  id: string;
  title: string;
  subtitle?: string;
  date?: string;
  status?: string;
  value?: string | number;
}

interface TableRow {
  id: string;
  [key: string]: string | number | boolean | undefined;
}

interface TableColumn {
  key: string;
  label: string;
  format?: 'currency' | 'percentage' | 'multiple' | 'number';
  align?: 'left' | 'right' | 'center';
}

interface WidgetConfig {
  data?: ChartData[];
  scatterData?: ScatterData[];
  activities?: ActivityItem[];
  items?: ListItemData[];
  tableData?: { columns: TableColumn[]; rows: TableRow[] };
  value?: number | string;
  label?: string;
  secondaryLabel?: string;
  trend?: number;
  color?: 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success';
  format?: 'number' | 'currency' | 'percentage' | 'multiple';
  layout?: 'horizontal' | 'vertical';
  showLabels?: boolean;
  showLegend?: boolean;
  showQuadrants?: boolean;
  orientation?: 'horizontal' | 'vertical';
  stages?: { label: string; value: number; color?: string }[];
  xAxisLabel?: string;
  yAxisLabel?: string;
}

interface Widget {
  id: string;
  name: string;
  chart_type: string;
  chart_config: WidgetConfig;
  query_config: Record<string, unknown>;
  width?: number;
  height?: number;
  position?: number;
  grid_x?: number;
  grid_y?: number;
  module?: string;
}

interface WidgetRendererProps {
  widget: Widget;
  height?: number;
}

function formatValue(value: number | string, format?: string): string {
  if (typeof value === 'string') return value;
  switch (format) {
    case 'currency':
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0,
      }).format(value);
    case 'percentage':
      return `${value.toFixed(1)}%`;
    case 'multiple':
      return `${value.toFixed(2)}x`;
    default:
      return value.toLocaleString();
  }
}

function KPICard({ config }: { config: WidgetConfig }) {
  const { value = 0, label, secondaryLabel, trend, color = 'primary', format } = config;
  const trendPositive = trend && trend > 0;
  const trendNegative = trend && trend < 0;

  const colorMap: Record<string, string> = {
    primary: 'primary.main',
    secondary: 'secondary.main',
    error: 'error.main',
    warning: 'warning.main',
    info: 'info.main',
    success: 'success.main',
  };

  return (
    <Box
      sx={{
        textAlign: 'center',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        py: 1,
      }}
    >
      <Typography variant="h4" sx={{ color: colorMap[color], fontWeight: 700, lineHeight: 1.2 }}>
        {formatValue(value, format)}
      </Typography>
      {label && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {label}
        </Typography>
      )}
      {secondaryLabel && (
        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.25 }}>
          {secondaryLabel}
        </Typography>
      )}
      {trend !== undefined && (
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
          {trendPositive && <TrendingUp size={14} color="green" />}
          {trendNegative && <TrendingDown size={14} color="red" />}
          <Typography
            variant="caption"
            sx={{
              color: trendPositive ? 'success.main' : trendNegative ? 'error.main' : 'text.secondary',
            }}
          >
            {trend > 0 ? '+' : ''}
            {trend}%
          </Typography>
        </Box>
      )}
    </Box>
  );
}

function FunnelChart({ config }: { config: WidgetConfig }) {
  const { stages = [] } = config;
  const maxValue = Math.max(...stages.map((s) => s.value), 1);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, py: 2 }}>
      {stages.map((stage, index) => {
        const widthPercent = (stage.value / maxValue) * 100;
        const prevStage = index > 0 ? stages[index - 1] : null;
        const conversionRate = prevStage && prevStage.value > 0 ? ((stage.value / prevStage.value) * 100).toFixed(0) : null;

        return (
          <Box key={stage.label} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ width: 120, flexShrink: 0 }}>
              <Typography variant="body2" noWrap>
                {stage.label}
              </Typography>
            </Box>
            <Box sx={{ flex: 1, position: 'relative' }}>
              <Box
                sx={{
                  height: 32,
                  width: `${widthPercent}%`,
                  bgcolor: stage.color || 'primary.main',
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center',
                  px: 1,
                  minWidth: 40,
                }}
              >
                <Typography variant="body2" sx={{ color: 'white', fontWeight: 600 }}>
                  {stage.value}
                </Typography>
              </Box>
            </Box>
            {conversionRate && (
              <Typography variant="caption" color="text.secondary" sx={{ width: 50 }}>
                {conversionRate}%
              </Typography>
            )}
          </Box>
        );
      })}
    </Box>
  );
}

function ActivityFeed({ config }: { config: WidgetConfig }) {
  const { activities = [] } = config;

  const getIcon = (type: string) => {
    switch (type) {
      case 'meeting':
        return <Calendar size={16} />;
      case 'message':
        return <MessageSquare size={16} />;
      case 'document':
        return <FileText size={16} />;
      case 'deal':
        return <DollarSign size={16} />;
      case 'contact':
        return <Users size={16} />;
      default:
        return <Building2 size={16} />;
    }
  };

  if (activities.length === 0) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">No recent activity</Typography>
      </Box>
    );
  }

  return (
    <List dense sx={{ py: 0 }}>
      {activities.map((activity) => (
        <ListItem key={activity.id} sx={{ px: 0 }}>
          <Avatar sx={{ width: 32, height: 32, mr: 1.5, bgcolor: 'action.selected' }}>{getIcon(activity.type)}</Avatar>
          <ListItemText primary={activity.description} secondary={activity.timestamp} primaryTypographyProps={{ variant: 'body2' }} secondaryTypographyProps={{ variant: 'caption' }} />
        </ListItem>
      ))}
    </List>
  );
}

function ListWidget({ config }: { config: WidgetConfig }) {
  const { items = [] } = config;

  if (items.length === 0) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">No items</Typography>
      </Box>
    );
  }

  return (
    <List dense sx={{ py: 0 }}>
      {items.map((item) => (
        <ListItem key={item.id} sx={{ px: 0 }}>
          <ListItemText primary={item.title} secondary={item.subtitle} primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }} secondaryTypographyProps={{ variant: 'caption' }} />
          {item.status && <Chip label={item.status} size="small" color={item.status === 'overdue' ? 'error' : item.status === 'urgent' ? 'warning' : 'default'} />}
          {item.date && (
            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              {item.date}
            </Typography>
          )}
        </ListItem>
      ))}
    </List>
  );
}

function formatTableValue(value: string | number | boolean | undefined, format?: string): string {
  if (value === undefined || value === null) return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'string') return value;
  switch (format) {
    case 'currency':
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0,
      }).format(value);
    case 'percentage':
      return `${value.toFixed(1)}%`;
    case 'multiple':
      return `${value.toFixed(2)}x`;
    default:
      return value.toLocaleString();
  }
}

function TableWidget({ config }: { config: WidgetConfig }) {
  const { tableData } = config;
  if (!tableData || !tableData.columns.length || !tableData.rows.length) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">No data available</Typography>
      </Box>
    );
  }
  const { columns, rows } = tableData;
  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
        <Box component="thead">
          <Box component="tr" sx={{ borderBottom: '2px solid', borderColor: 'divider' }}>
            {columns.map((col) => (
              <Box
                component="th"
                key={col.key}
                sx={{
                  py: 1,
                  px: 1.5,
                  textAlign: col.align || 'left',
                  fontWeight: 600,
                  color: 'text.secondary',
                  whiteSpace: 'nowrap',
                }}
              >
                {col.label}
              </Box>
            ))}
          </Box>
        </Box>
        <Box component="tbody">
          {rows.map((row) => (
            <Box
              component="tr"
              key={row.id}
              sx={{
                borderBottom: '1px solid',
                borderColor: 'divider',
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              {columns.map((col) => (
                <Box component="td" key={col.key} sx={{ py: 1, px: 1.5, textAlign: col.align || 'left', whiteSpace: 'nowrap' }}>
                  {formatTableValue(row[col.key], col.format)}
                </Box>
              ))}
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

function CalendarWidget({ config }: { config: WidgetConfig }) {
  const { items = [] } = config;

  if (items.length === 0) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">No upcoming events</Typography>
      </Box>
    );
  }

  return (
    <List dense sx={{ py: 0 }}>
      {items.map((item) => (
        <ListItem key={item.id} sx={{ px: 0 }}>
          <Avatar sx={{ width: 32, height: 32, mr: 1.5, bgcolor: 'primary.main' }}>
            <Calendar size={16} />
          </Avatar>
          <ListItemText primary={item.title} secondary={item.date} primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }} secondaryTypographyProps={{ variant: 'caption' }} />
        </ListItem>
      ))}
    </List>
  );
}

function PlaceholderWidget({ chartType }: { chartType: string }) {
  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'action.hover',
        borderRadius: 1,
      }}
    >
      <Typography color="text.secondary">{chartType} widget (no data)</Typography>
    </Box>
  );
}

export function WidgetRenderer({ widget, height = 320, module }: WidgetRendererProps & { module?: string }) {
  const { chart_type, chart_config, name } = widget;

  // Use mock data if chart_config is empty/missing data
  // This allows widgets to render with placeholder data during development
  const mockData = useMockData(module || widget.module, name, chart_type);
  const config = hasData(chart_config) ? chart_config : { ...chart_config, ...mockData };

  switch (chart_type) {
    case 'kpi_card':
      return <KPICard config={config} />;

    case 'funnel':
      return <FunnelChart config={config} />;

    case 'activity_feed':
      return <ActivityFeed config={config} />;

    case 'list':
      return <ListWidget config={config} />;

    case 'table':
      return <TableWidget config={config} />;

    case 'calendar':
      return <CalendarWidget config={config} />;

    case 'bar': {
      if (!config.data?.length) return <PlaceholderWidget chartType={chart_type} />;
      const isHorizontal = config.orientation === 'horizontal';
      // For horizontal bars, band scale goes on yAxis; for vertical, on xAxis
      return (
        <BarChart
          xAxis={isHorizontal ? undefined : [{ scaleType: 'band', data: config.data.map((d: ChartData) => d.label) }]}
          yAxis={isHorizontal ? [{ scaleType: 'band', data: config.data.map((d: ChartData) => d.label) }] : undefined}
          series={[{ data: config.data.map((d: ChartData) => d.value) }]}
          height={height}
          margin={{ left: isHorizontal ? 100 : 50, right: 20, top: 20, bottom: 50 }}
          layout={isHorizontal ? 'horizontal' : 'vertical'}
        />
      );
    }

    case 'line':
      if (!config.data?.length) return <PlaceholderWidget chartType={chart_type} />;
      return (
        <LineChart
          xAxis={[{ scaleType: 'point', data: config.data.map((d: ChartData) => d.label) }]}
          series={[{ data: config.data.map((d: ChartData) => d.value) }]}
          height={height}
          margin={{ left: 50, right: 20, top: 20, bottom: 50 }}
        />
      );

    case 'pie':
      if (!config.data?.length) return <PlaceholderWidget chartType={chart_type} />;
      return (
        <PieChart
          series={[
            {
              data: config.data.map((d: ChartData, i: number) => ({
                id: i,
                value: d.value,
                label: d.label,
              })),
              innerRadius: 30,
              paddingAngle: 2,
              cornerRadius: 4,
              arcLabel: (item) => `${item.value}`,
              arcLabelMinAngle: 20,
            },
          ]}
          height={height}
          margin={{ left: 20, right: 200, top: 20, bottom: 20 }}
        />
      );

    case 'scatter': {
      if (!config.scatterData?.length) return <PlaceholderWidget chartType={chart_type} />;
      const xLabel = config.xAxisLabel || 'X';
      const yLabel = config.yAxisLabel || 'Y';
      return (
        <ScatterChart
          series={[
            {
              data: config.scatterData.map((d: ScatterData) => ({
                x: d.x,
                y: d.y,
                id: d.id,
                label: d.label,
              })),
              valueFormatter: (v) => {
                if (!v) return '';
                const point = config.scatterData?.find((d: ScatterData) => d.x === v.x && d.y === v.y);
                const name = point?.label || 'Unknown';
                return `${name}\n${xLabel}: ${v.x} mo\n${yLabel}: $${v.y}K`;
              },
            },
          ]}
          xAxis={[{ label: xLabel, min: 0 }]}
          yAxis={[{ label: yLabel, min: 0 }]}
          height={height}
          margin={{ left: 70, right: 20, top: 20, bottom: 50 }}
        />
      );
    }

    case 'sparkline':
      if (!config.data?.length) return <PlaceholderWidget chartType={chart_type} />;
      return <SparkLineChart data={config.data.map((d: ChartData) => d.value)} height={60} showTooltip showHighlight />;

    default:
      return <PlaceholderWidget chartType={chart_type} />;
  }
}

export type { Widget, WidgetConfig, ChartData, ScatterData, ActivityItem, ListItemData, TableRow, TableColumn };
